<template>
  <v-container fluid :class="baseStore?.isDesktop ? 'pa-8' : 'pa-4'">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="9">
        <div class="d-flex align-center justify-space-between mb-6">
          <h1 class="my-webinare-title">Meine Webinare</h1>
          <v-btn variant="outlined" size="small" @click="loadMeineWebinare">
            Aktualisieren
          </v-btn>
        </div>

        <v-alert v-if="!userStore?.userData" type="info" variant="tonal" class="mb-4">
          Bitte zuerst einloggen, um Ihre Webinare zu sehen.
        </v-alert>

        <v-alert v-else-if="webinars.length === 0" type="warning" variant="tonal" class="mb-4">
          Für diesen Account sind aktuell keine Webinar-Teilnahmen vorhanden.
        </v-alert>

        <WebinarCard
          v-for="(webinar, index) in webinars"
          :key="`${webinar.terminId}-${index}`"
          :webinar="webinar"
          :base-store="baseStore"
        />
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { inject, onMounted, ref } from 'vue';
import WebinarCard from '@/components/objects/webinar/WebinarCard.vue';
import http from '@/services/http.js';

const baseCalls = inject('baseCalls');
const baseStore = inject('baseStore');
const loaderStore = inject('loaderStore');
const userStore = inject('userStore');
const updateDocumentHead = inject('updateDocumentHead');

const webinars = ref([]);
const WEBINAR_BASE_URL = 'https://www.pickpoint.live';
const DEBUG_ALWAYS_SHOW_START_LINK = false;

updateDocumentHead?.(
  'PickPoint - Meine Webinare',
  'Ihre gebuchten und kommenden PickPoint Webinare'
);

function resolveDozentImage(imageName) {
  if (!imageName) return null;
  if (typeof imageName !== 'string') return null;
  if (imageName.startsWith('/')) return imageName;

  try {
    return require(`@/assets/dozenten/${imageName}`);
  } catch (e) {
    return `/dozenten/${imageName}`;
  }
}

function getUserId() {
  const user = userStore?.userData || {};
  return user?.user_id || user?.user?.user_id || 0;
}

async function loadMeineWebinare() {
  const userId = Number(getUserId());
  if (!userId) {
    webinars.value = [];
    return;
  }

  try {
    loaderStore?.setLoader?.(true, 'Ihre Webinare werden geladen...');
    const isDozent = Number(userStore?.userData?.user_is_dozent) === 1;
    let rows = [];
    if (isDozent) {
      const [dozentResponse, allResponse] = await Promise.all([
        baseCalls.webinarPost({ action: 'getMeineWebinareDozent', user_id: userId }),
        baseCalls.webinarPost({ action: 'getMeineWebinare', user_id: userId })
      ]);
      const dozentRows = Array.isArray(dozentResponse?.data?.webinare) ? dozentResponse.data.webinare : [];
      const allRows = Array.isArray(allResponse?.data?.webinare) ? allResponse.data.webinare : [];
      const seen = new Set();
      rows = [...dozentRows, ...allRows].filter(item => {
        const key = item.wteil_id || `${item.wter_id}-${item.wteil_user_id}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    } else {
      const response = await baseCalls.webinarPost({
        action: 'getMeineWebinare',
        user_id: userId
      });
      rows = Array.isArray(response?.data?.webinare) ? response.data.webinare : [];
    }

    webinars.value = rows.map((item) => {
      const startsAt = item.wter_beginn || null;
      const startsDate = startsAt ? new Date(startsAt) : null;
      const startsAtTs = startsDate && !Number.isNaN(startsDate.getTime()) ? startsDate.getTime() : 0;
      const oneDayAgoTs = Date.now() - (24 * 60 * 60 * 1000);
      const showStartNow = !!item.wteil_id
        && !!item.wteil_audience_hash
        && (DEBUG_ALWAYS_SHOW_START_LINK || startsAtTs >= oneDayAgoTs);
      const isDozentTeilnahme = Number(item.wteil_is_dozent || 0) === 1;
      const isAdminTeilnahme = Number(item.wteil_is_admin || 0) === 1;
      const termin = startsDate && !Number.isNaN(startsDate.getTime())
        ? startsDate.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : null;

      // Teilnahme: echtes Attended-Flag oder Token-Signal (Altbestand)
      const attended = !isDozentTeilnahme
        && (Number(item.wteil_attended || 0) === 1
          || String(item.wteil_audience_token || '').trim() !== '');

      let startNowUrl = '';
      if (showStartNow) {
        if (isAdminTeilnahme || isDozentTeilnahme) {
          startNowUrl = `${WEBINAR_BASE_URL}/dozent/${item.wteil_id}/${item.wteil_audience_hash}`;
        } else if (item.wteil_browser_token) {
          startNowUrl = `${WEBINAR_BASE_URL}/watch/${item.wteil_id}/${item.wteil_audience_hash}/${item.wteil_browser_token}`;
        } else {
          startNowUrl = `${WEBINAR_BASE_URL}/watch/${item.wteil_id}/${item.wteil_audience_hash}`;
        }
      }

      return {
        terminId: item.wter_id,
        title: item.webi_title || 'Webinar',
        description: item.webi_description || item.webi_teaser || '',
        icon: 'mdi-school',
        color: 'neutral',
        dozent: item.webi_dozent || 'Dozent',
        dauer: item.webi_laenge || 15,
        termin,
        startsAt,
        showStartNow,
        startNowUrl,
        isDozentTeilnahme,
        isAdminTeilnahme,
        attended,
        zertifikatUrl: attended && item.wteil_id
          ? `${http.defaults.baseURL}/zertifikatpdf/${item.wteil_id}`
          : '',
        dozentImage: resolveDozentImage(item.dozent_image),
        termine: []
      };
    });
  } catch (error) {
    console.error('Fehler beim Laden meiner Webinare:', error);
    webinars.value = [];
  } finally {
    loaderStore?.setLoader?.(false);
  }
}

onMounted(() => {
  loadMeineWebinare();
});
</script>

<style scoped>
.my-webinare-title {
  margin: 0;
  color: #374151;
  font-size: clamp(1.5rem, 2.5vw, 2rem);
  font-weight: 700;
}
</style>
